import { db } from '../config/firebase';
import { doc, setDoc, getDoc, Timestamp, updateDoc, increment } from 'firebase/firestore';
import { trackStartTrial } from '../utils/metaPixel';

export interface TrialInfo {
  userId: string;
  email: string;
  startTime: Date;
  endTime: Date;
  isExpired: boolean;
  timeRemaining: number; // em milissegundos
  daysRemaining: number;
  hoursRemaining: number;
  minutesRemaining: number;
  /** Isenção permanente concedida no admin-monitoring */
  isExempt?: boolean;
}

export interface TrialUser {
  uid: string;
  email: string;
  displayName: string;
  trialStartTime: Timestamp;
  trialEndTime: Timestamp;
  isTrialActive: boolean;
  loginCount: number;
  createdAt: Timestamp;
  clinicId: string;
}

const MAX_RETRIES = 1;
const RETRY_DELAY = 500; // 0.5 seconds

/** Detecta se o erro é de cliente offline ou erro interno do Firebase (não adianta retentar). */
function isOfflineOrInternalError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: string; message?: string };
  const code = e.code;
  const msg = (e.message || '').toLowerCase();
  return (
    code === 'unavailable' ||
    code === 'internal' ||
    msg.includes('client is offline') ||
    msg.includes('failed to get document')
  );
}

class TrialService {
  private readonly TRIAL_DURATION_DAYS = 7; // 7 dias de teste
  private readonly TRIAL_DURATION_HOURS = 168; // 7 dias = 168 horas
  private readonly TRIAL_COLLECTION = 'trialUsers';

  /**
   * 🕒 Inicia um novo trial para um usuário
   */
  async startTrial(userId: string, email: string): Promise<void> {
    console.log('🆕 [TRIAL] Iniciando trial para', email);
    
    if (!userId || !email) {
      throw new Error('UserId e email são obrigatórios para iniciar o trial');
    }

    const trialRef = doc(db, this.TRIAL_COLLECTION, userId);
    
    // Verificar se já existe um trial
    try {
      const existingTrial = await getDoc(trialRef);
      if (existingTrial.exists()) {
        console.log('⚠️ [TRIAL] Trial já existe para', email);
        return;
      }
    } catch (error) {
      console.error('❌ [TRIAL] Erro ao verificar trial existente:', error);
      // Se for erro de permissão, logar detalhadamente e não lançar erro
      if (error && error.code === 'permission-denied') {
        console.error('❌ [TRIAL] Permissão negada para criar trial. Verifique as regras do Firestore.');
        return;
      }
      // Continuar mesmo se a verificação falhar - vamos tentar criar um novo
    }
    
    // Create trial data with exact 7-day period
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Zerar horas para garantir exatidão
    const endDate = new Date(now);
    endDate.setDate(endDate.getDate() + this.TRIAL_DURATION_DAYS); // Exatamente 7 dias
    
    const clinicId = this.generateUniqueClinicId(userId);
    
    // Save backup to localStorage first
    this.saveTrialToLocalStorage(userId, now, endDate, clinicId);

    // Improved retry mechanism with exponential backoff
    let lastError = null;
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        // Criar dados do trial exatamente como especificado nas regras
        const trialData = {
          startDate: Timestamp.fromDate(now),
          endDate: Timestamp.fromDate(endDate),
          email,
          status: 'active',
          lastLogin: Timestamp.fromDate(now),
          loginCount: 1,
          clinicId
        };

        await setDoc(trialRef, trialData);
        console.log('✅ [TRIAL] Trial iniciado com sucesso para', email);
        // Distinct trial-start moment for Meta ads (no PHI — user key for dedupe only)
        trackStartTrial(userId);
        return;
      } catch (error) {
        lastError = error;
        if (isOfflineOrInternalError(error)) {
          // Backup já foi salvo no localStorage; não poluir console
          return;
        }
        console.warn(`⚠️ [TRIAL] Tentativa ${attempt} falhou:`, (error as any)?.message || error);
        if (attempt < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, attempt - 1))); // Exponential backoff
        }
      }
    }

    if (lastError && !isOfflineOrInternalError(lastError)) {
      console.warn('⚠️ [TRIAL] Erro ao iniciar trial (Firestore):', (lastError as any)?.message || lastError);
    }
    // Não lançar erro, apenas logar
    return;
  }

  /**
   * Converte Timestamp / Date / string / seconds em Date.
   */
  private toDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value?.toDate === 'function') {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    }
    if (typeof value?.seconds === 'number') {
      const d = new Date(value.seconds * 1000);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
  }

  /**
   * 🔍 Verifica o status do trial de um usuário
   * Respeita isenção (users.isento) e prorrogação (users.dataExpiracao) do admin-monitoring.
   */
  async getTrialInfo(userId: string, userEmail: string): Promise<TrialInfo | null> {
    try {
      // 1) Isenção / data manual no documento do usuário (fonte do admin)
      let userIsento = false;
      let userDataExpiracao: Date | null = null;
      try {
        const userSnap = await getDoc(doc(db, 'users', userId));
        if (userSnap.exists()) {
          const u = userSnap.data();
          userIsento = u.isento === true;
          userDataExpiracao = this.toDate(u.dataExpiracao);
        }
      } catch (e: any) {
        if (e?.code !== 'permission-denied' && !isOfflineOrInternalError(e)) {
          console.warn('⚠️ [TRIAL] Não foi possível ler users/ para isento/dataExpiracao:', e?.message || e);
        }
      }

      if (userIsento) {
        const far = new Date();
        far.setFullYear(far.getFullYear() + 50);
        return {
          userId,
          email: userEmail,
          startTime: new Date(),
          endTime: far,
          isExpired: false,
          timeRemaining: far.getTime() - Date.now(),
          daysRemaining: 99999,
          hoursRemaining: 99999,
          minutesRemaining: 99999,
          isExempt: true,
        };
      }

      // 2) Trial canônico em trialUsers
      let retryCount = 0;
      const maxRetries = 3;
      let trialDoc = null;

      while (retryCount < maxRetries) {
        const currentRetry = retryCount;
        try {
          const trialRef = doc(db, this.TRIAL_COLLECTION, userId);
          trialDoc = await getDoc(trialRef);
          break;
        } catch (error: any) {
          if (error?.code === 'permission-denied') {
            // Sem trialUsers: ainda pode haver dataExpiracao no users
            if (userDataExpiracao) {
              const start = new Date(userDataExpiracao.getTime() - 7 * 24 * 60 * 60 * 1000);
              return this.calculateTrialInfo(userId, userEmail, start, userDataExpiracao);
            }
            return null;
          }
          if (isOfflineOrInternalError(error)) {
            const localTrial = this.getTrialFromLocalStorage(userId);
            if (localTrial) {
              let end = localTrial.endTime;
              if (userDataExpiracao && userDataExpiracao > end) end = userDataExpiracao;
              return this.calculateTrialInfo(userId, userEmail, localTrial.startTime, end);
            }
            if (userDataExpiracao) {
              const start = new Date(userDataExpiracao.getTime() - 7 * 24 * 60 * 60 * 1000);
              return this.calculateTrialInfo(userId, userEmail, start, userDataExpiracao);
            }
            return null;
          }
          console.warn(`⚠️ [TRIAL] Tentativa ${currentRetry + 1} falhou:`, error?.message || error);
          retryCount++;
          if (retryCount === maxRetries) throw error;
          await new Promise((resolve) => setTimeout(resolve, 1000 * Math.pow(2, currentRetry)));
        }
      }

      // Isenção gravada só em trialUsers (status exempt / isento)
      if (trialDoc && trialDoc.exists()) {
        const td = trialDoc.data();
        if (td.isento === true || td.status === 'exempt') {
          const far = new Date();
          far.setFullYear(far.getFullYear() + 50);
          return {
            userId,
            email: userEmail || td.email || '',
            startTime: this.toDate(td.startDate) || this.toDate(td.startTime) || new Date(),
            endTime: far,
            isExpired: false,
            timeRemaining: far.getTime() - Date.now(),
            daysRemaining: 99999,
            hoursRemaining: 99999,
            minutesRemaining: 99999,
            isExempt: true,
          };
        }
      }

      let startTime: Date | null = null;
      let endTime: Date | null = null;

      if (trialDoc && trialDoc.exists()) {
        const data = trialDoc.data();
        startTime =
          this.toDate(data.startDate) ||
          this.toDate(data.startTime) ||
          this.toDate(data.trialStartTime);
        endTime =
          this.toDate(data.endDate) ||
          this.toDate(data.endTime) ||
          this.toDate(data.trialEndTime);
      }

      // Fallback localStorage
      if (!endTime) {
        const localTrial = this.getTrialFromLocalStorage(userId);
        if (localTrial) {
          startTime = localTrial.startTime;
          endTime = localTrial.endTime;
        }
      }

      // Fallback trials/ (legado)
      if (!endTime) {
        try {
          const legacy = await getDoc(doc(db, 'trials', userId));
          if (legacy.exists()) {
            const d = legacy.data();
            if (d.isento === true) {
              const far = new Date();
              far.setFullYear(far.getFullYear() + 50);
              return {
                userId,
                email: userEmail,
                startTime: this.toDate(d.startDate) || new Date(),
                endTime: far,
                isExpired: false,
                timeRemaining: far.getTime() - Date.now(),
                daysRemaining: 99999,
                hoursRemaining: 99999,
                minutesRemaining: 99999,
                isExempt: true,
              };
            }
            startTime = startTime || this.toDate(d.startDate) || this.toDate(d.startTime);
            endTime = this.toDate(d.endDate) || this.toDate(d.endTime);
          }
        } catch {
          /* ignore */
        }
      }

      // Prorrogação do admin: usar a data mais recente entre trial e users.dataExpiracao
      if (userDataExpiracao) {
        if (!endTime || userDataExpiracao.getTime() > endTime.getTime()) {
          endTime = userDataExpiracao;
        }
      }

      if (!endTime) {
        return null;
      }

      if (!startTime) {
        startTime = new Date(endTime.getTime() - 7 * 24 * 60 * 60 * 1000);
      }

      const trialInfo = this.calculateTrialInfo(userId, userEmail, startTime, endTime);
      if (!trialInfo.isExpired && trialDoc?.exists()) {
        await this.updateLoginCount(userId);
      }
      return trialInfo;
    } catch (error) {
      if (!isOfflineOrInternalError(error)) {
        console.warn('⚠️ [TRIAL] Erro ao verificar trial:', (error as any)?.message || error);
      }
      const localTrial = this.getTrialFromLocalStorage(userId);
      if (localTrial) {
        return this.calculateTrialInfo(userId, userEmail, localTrial.startTime, localTrial.endTime);
      }
      return null;
    }
  }

  /**
   * 🚫 Encerra o trial de um usuário
   */
  async endTrial(userId: string): Promise<void> {
    try {
      // Atualizar no Firestore
      const trialRef = doc(db, this.TRIAL_COLLECTION, userId);
      await setDoc(trialRef, { isTrialActive: false }, { merge: true });
      
      // Remover do localStorage
      localStorage.removeItem(`trial_${userId}`);
      
      console.log(`🛑 [TRIAL] Trial encerrado para usuário: ${userId}`);
    } catch (error) {
      console.error('❌ [TRIAL] Erro ao encerrar trial:', error);
    }
  }

  /**
   * 📊 Calcula informações do trial
   */
  private calculateTrialInfo(userId: string, userEmail: string, startTime: Date, endTime: Date): TrialInfo {
    const now = new Date();
    const timeRemaining = Math.max(0, endTime.getTime() - now.getTime());
    const isExpired = timeRemaining <= 0;
    
    const daysRemaining = Math.ceil(timeRemaining / (1000 * 60 * 60 * 24));
    const hoursRemaining = Math.floor(timeRemaining / (1000 * 60 * 60));
    const minutesRemaining = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));

    return {
      userId,
      email: userEmail,
      startTime,
      endTime,
      isExpired,
      timeRemaining,
      daysRemaining,
      hoursRemaining,
      minutesRemaining,
      isExempt: false,
    };
  }

  /**
   * 🔑 Gera clinicId único baseado no userId
   */
  private generateUniqueClinicId(userId: string): string {
    // clinicId do titular = uid (mesma regra do perfil em users/, validada nas regras do Firestore)
    return userId;
  }

  /**
   * 💾 Salva trial no localStorage (backup)
   */
  public saveTrialToLocalStorage(userId: string, startTime: Date, endTime: Date, clinicId: string): void {
    const trialData = {
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      clinicId,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem(`trial_${userId}`, JSON.stringify(trialData));
  }

  /**
   * 📖 Recupera trial do localStorage
   */
  public getTrialFromLocalStorage(userId: string): { startTime: Date; endTime: Date; clinicId: string } | null {
    try {
      const trialData = localStorage.getItem(`trial_${userId}`);
      if (trialData) {
        const parsed = JSON.parse(trialData);
        return {
          startTime: new Date(parsed.startTime),
          endTime: new Date(parsed.endTime),
          clinicId: parsed.clinicId
        };
      }
    } catch (error) {
      console.error('❌ [TRIAL] Erro ao ler localStorage:', error);
    }
    return null;
  }

  /**
   * 📈 Atualiza contador de login com increment() atômico (sem leitura prévia)
   */
  private async updateLoginCount(userId: string): Promise<void> {
    try {
      const trialRef = doc(db, this.TRIAL_COLLECTION, userId);
      await updateDoc(trialRef, {
        loginCount: increment(1),
        lastLogin: Timestamp.fromDate(new Date())
      });
    } catch (error) {
      // Silenciar erro — não é crítico para o login
    }
  }

  /**
   * 🧹 Limpa trials expirados (limpeza automática)
   */
  async cleanupExpiredTrials(): Promise<void> {
    // Esta função pode ser chamada periodicamente para limpar dados antigos
    console.log('🧹 [TRIAL] Limpeza de trials expirados executada');
  }

  /**
   * 📊 Obtém estatísticas de trials
   */
  async getTrialStats(): Promise<{
    totalTrials: number;
    activeTrials: number;
    expiredTrials: number;
  }> {
    // Implementação para admin ver estatísticas
    return {
      totalTrials: 0,
      activeTrials: 0,
      expiredTrials: 0
    };
  }

  /**
   * ⚡ Verifica se usuário deve ser redirecionado para vendas
   */
  shouldRedirectToSales(trialInfo: TrialInfo | null): boolean {
    if (!trialInfo || trialInfo.isExempt) return false;
    return trialInfo.isExpired;
  }

  /**
   * 🎯 Obtém URL de redirecionamento baseado no status
   */
  getRedirectUrl(trialInfo: TrialInfo | null): string {
    if (!trialInfo) return '/'; // Usuário sem trial
    
    if (trialInfo.isExpired) {
      return '/?redirect=expired'; // Redirecionar para landing page com modal de vendas
    }
    
    return '/dashboard'; // Continuar usando o sistema
  }

  /**
   * 💡 Obtém mensagem de status do trial
   */
  getTrialStatusMessage(trialInfo: TrialInfo): string {
    if (trialInfo.isExempt) {
      return '';
    }

    if (trialInfo.isExpired) {
      return 'Seu período de teste de 7 dias expirou. Adquira um plano para continuar usando o UpClinic.';
    }
    
    const daysRemaining = Math.floor(trialInfo.hoursRemaining / 24);
    const hoursRemainingInDay = trialInfo.hoursRemaining % 24;
    
    if (trialInfo.hoursRemaining <= 1) {
      return `⏰ Seu teste expira em ${trialInfo.minutesRemaining} minutos. Não perca o acesso!`;
    }
    
    if (trialInfo.hoursRemaining <= 24) {
      return `⚠️ Último dia do teste! Restam ${trialInfo.hoursRemaining}h ${trialInfo.minutesRemaining}min.`;
    }
    
    if (daysRemaining >= 1) {
      return `✅ Teste ativo: ${daysRemaining} dia(s) e ${hoursRemainingInDay}h restantes.`;
    }
    
    return `✅ Teste ativo: ${trialInfo.hoursRemaining}h ${trialInfo.minutesRemaining}min restantes.`;
  }

  async verifyTrial(userId: string): Promise<boolean> {
    console.log('🔍 [TRIAL] Verificando trial para', userId);
    
    let lastError = null;
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const trialDoc = await getDoc(doc(db, 'trialUsers', userId));
        
        if (!trialDoc.exists()) {
          return false;
        }

        const trialData = trialDoc.data();
        const now = Date.now();
        
        // Check if trial is still valid
        if (now <= trialData.endDate && trialData.status === 'active') {
          return true;
        }

        // If trial expired, update status
        if (trialData.status === 'active') {
          await updateDoc(doc(db, 'trialUsers', userId), {
            status: 'expired'
          });
        }
        
        return false;
      } catch (error) {
        lastError = error;
        if (isOfflineOrInternalError(error)) {
          // Offline: tentar decidir pelo localStorage
          const local = this.getTrialFromLocalStorage(userId);
          if (local) {
            const now = Date.now();
            return now <= local.endTime.getTime();
          }
          return false;
        }
        if (attempt < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        }
      }
    }

    // Silenciar erro de permissão para subordinate_users
    if ((lastError as any)?.code === 'permission-denied') {
      return false; // Subordinate users não têm trial próprio
    }

    if (lastError && !isOfflineOrInternalError(lastError)) {
      console.warn('⚠️ [TRIAL] Erro ao verificar trial:', (lastError as any)?.message || lastError);
    }
    throw new Error('Não foi possível verificar o período de teste. Por favor, tente novamente.');
  }
}

// Instância singleton
export const trialService = new TrialService();
export default trialService; 